// Languages across the Smart CMS: naming, direction, per-record translation status and coverage.
// All three sections describe a language and its progress through this one module

import { SITE_LANGUAGES, SITE_LOCALE_CODES, siteLocaleFor, unsupportedLocaleMessage } from "./siteLanguages"

export type Direction = "ltr" | "rtl"

export interface LanguageInfo {
  code: string
  name: string // "Spanish (Mexico)"
  direction: Direction
}

// Where one record stands in one language
export type TranslationStatus =
  | "source"     // the record's source language
  | "missing"    // no text in this language
  | "unreviewed" // text a person hasn't checked
  | "reviewed"   // text a person has checked
  | "translated" // text, where review isn't tracked (site header and footer)

export const STATUS_LABEL: Record<TranslationStatus, string> = {
  source: "Source",
  missing: "Missing",
  unreviewed: "Needs review",
  reviewed: "Reviewed",
  translated: "Translated",
}

// Present on every translation whether or not anyone wrote anything
const BOOKKEEPING_KEYS = new Set(["locale", "updatedAt", "reviewed", "state", "status"])

// True when a translation holds real text, not just bookkeeping or cleared fields
export function hasTranslationContent(t: unknown): boolean {
  if (!t || typeof t !== "object") return false
  return Object.entries(t as Record<string, unknown>).some(([k, v]) => {
    if (BOOKKEEPING_KEYS.has(k)) return false
    return typeof v === "string" ? v.trim() !== "" : v != null
  })
}

export function translationStatus(
  translations: Record<string, any> | null | undefined,
  locale: string,
  sourceLocale: string,
): TranslationStatus {
  if (locale === sourceLocale) return "source"
  const t = translations?.[locale]
  if (!hasTranslationContent(t)) return "missing"
  return t.reviewed ? "reviewed" : "unreviewed"
}

export interface Coverage {
  total: number
  translated: number // has text, reviewed or not
  reviewed: number
  unreviewed: number
  missing: number
}

export function coverageOf(
  records: Iterable<{ translations?: Record<string, any> | null }>,
  locale: string,
  sourceLocale: string,
): Coverage {
  const c: Coverage = { total: 0, translated: 0, reviewed: 0, unreviewed: 0, missing: 0 }
  for (const r of records) {
    c.total++
    const t = r.translations?.[locale]
    if (!hasTranslationContent(t)) { c.missing++; continue }
    c.translated++
    // The source language counts as checked: it's what everything else is checked against
    if (locale === sourceLocale || t.reviewed) c.reviewed++
    else c.unreviewed++
  }
  return c
}

export const percent = (c: Coverage) => (c.total ? Math.round((c.translated / c.total) * 100) : 0)

// ── Names and direction ─────────────────────────────────────────────────────

// Primary subtags written right to left
const RTL = new Set(["ar", "fa", "he", "ur", "ps", "ckb", "dv", "yi", "sd", "ug", "syr", "prs", "ks"])

export function directionOf(code: string): Direction {
  return RTL.has(code.toLowerCase().split("-")[0]) ? "rtl" : "ltr"
}

let displayNames: Intl.DisplayNames | null | undefined
function names(): Intl.DisplayNames | null {
  if (displayNames === undefined) {
    // "standard" gives "English (United States)" rather than "American English", matching "Spanish (Mexico)"
    try { displayNames = new Intl.DisplayNames(["en"], { type: "language", languageDisplay: "standard" }) } catch { displayNames = null }
  }
  return displayNames
}

// English name for a code: "es-MX" → "Spanish (Mexico)". Unknown codes come back as themselves
export function languageName(code: string): string {
  try {
    const n = names()?.of(code)
    return n && n !== code ? n : code
  } catch {
    return code
  }
}

// A language's name in itself ("العربية", "Español"): what the public site shows readers, and what gets saved
export function nativeLanguageName(code: string): string {
  if (SITE_LANGUAGES[code]) return SITE_LANGUAGES[code].name
  try {
    const n = new Intl.DisplayNames([code], { type: "language", languageDisplay: "standard" }).of(code)
    if (!n || n === code) return languageName(code)
    // Some languages don't capitalise their own names; a menu reads better when they do
    return n.charAt(0).toLocaleUpperCase(code) + n.slice(1)
  } catch {
    return languageName(code)
  }
}

// English names everywhere, so a list doesn't mix "Arabic" with "Español (España)".
// A saved name is only used for a code the browser can't name
export function describeLanguage(code: string, known?: { name?: string; direction?: Direction }): LanguageInfo {
  // The sites' own list names and directs its languages
  const site = SITE_LANGUAGES[code]
  if (site) return { code, name: site.english, direction: site.rtl ? "rtl" : "ltr" }
  const standard = languageName(code)
  const name = standard !== code ? standard : known?.name || code
  return { code, name, direction: known?.direction ?? directionOf(code) }
}

// The supported code for what was typed ("ar" → "ar-SA", "es-es" → "es-ES"), or why it can't be used.
// Only the languages the Signpost sites can show are accepted: see ./siteLanguages
export function normalizeLocale(input: string): { code: string; error?: undefined } | { code?: undefined; error: string } {
  const raw = input.trim()
  if (!raw) return { error: "Type a language, like Spanish, or a code like es-ES." }
  const code = siteLocaleFor(raw)
  return code ? { code } : { error: unsupportedLocaleMessage(raw) }
}

// Offered when adding a language: every language the sites support
export const SUGGESTED_LOCALES = SITE_LOCALE_CODES

// Suggestions for what's been typed, leaving out languages already added
export function suggestLocales(query: string, exclude: Iterable<string>, limit = 8): LanguageInfo[] {
  const taken = new Set([...exclude].map(c => c.toLowerCase()))
  const q = query.trim().toLowerCase()
  const pool = SUGGESTED_LOCALES.filter(c => !taken.has(c.toLowerCase())).map(c => describeLanguage(c))
  if (!q) return pool.slice(0, limit)
  return pool
    .filter(l => l.code.toLowerCase().startsWith(q) || l.name.toLowerCase().includes(q)
      || (SITE_LANGUAGES[l.code]?.name.toLowerCase().includes(q) ?? false))
    .slice(0, limit)
}

// Source language first, then by name
export function sortLanguages(langs: LanguageInfo[], sourceLocale: string): LanguageInfo[] {
  return [...langs].sort((a, b) =>
    a.code === sourceLocale ? -1 : b.code === sourceLocale ? 1 : a.name.localeCompare(b.name))
}
